import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const ROOT_SELECTOR = 'langley-cart-enhancements';
const FORM_COMPONENT_SELECTOR = '[data-langley-recommendation-form], [data-langley-panel-form-component]';

function getSubmitButton(component) {
  return component.querySelector('button[type="submit"]');
}

function setSubmitting(component, isSubmitting) {
  const button = getSubmitButton(component);
  if (!(button instanceof HTMLButtonElement)) return;

  if (isSubmitting) {
    if (!button.dataset.langleyOriginalText) {
      button.dataset.langleyOriginalText = button.textContent?.trim() || 'Add to Bag';
    }
    button.disabled = true;
    button.textContent = 'Adding…';
    return;
  }

  const variantInput = component.querySelector('input[name="id"]');
  const canSubmit =
    variantInput instanceof HTMLInputElement &&
    !variantInput.disabled &&
    Boolean(variantInput.value);

  button.disabled = !canSubmit;
  button.textContent = button.dataset.langleyOriginalText || 'Add to Bag';
}

function showSuccess(component) {
  const button = getSubmitButton(component);
  if (!(button instanceof HTMLButtonElement)) return;

  button.textContent = 'Added';
  window.setTimeout(() => {
    if (!button.isConnected) return;
    setSubmitting(component, false);
  }, 1200);
}

async function submitCompleteLookForm(root, form, component) {
  const variantInput = form.querySelector('input[name="id"]');
  if (!(variantInput instanceof HTMLInputElement) || variantInput.disabled || !variantInput.value) {
    const message = 'Please select your options first.';
    component.dispatchEvent(new CartErrorEvent(form.id || '', message, message, {}));
    setSubmitting(component, false);
    return;
  }

  setSubmitting(component, true);

  const formData = new FormData(form);
  const sectionIds = [
    ...new Set(
      [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : ''))
        .filter(Boolean)
    ),
  ];

  if (sectionIds.length) {
    formData.set('sections', sectionIds.join(','));
  }

  const fetchCfg = fetchConfig('javascript', { body: formData });

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    });
    const data = await response.json();

    if (data.status) {
      const message = data.message || data.description || 'We could not add this item. Please try again.';
      component.dispatchEvent(new CartErrorEvent(form.id || '', message, data.description, data.errors));
      setSubmitting(component, false);
      return;
    }

    const drawer = root.closest('cart-drawer-component');
    if (drawer instanceof HTMLElement) {
      drawer.dataset.langleyCartMessage = 'Added to your bag';
    }

    const quantity = Number(formData.get('quantity')) || 1;
    component.dispatchEvent(
      new CartAddEvent({}, variantInput.value.toString(), {
        source: 'product-form-component',
        itemCount: quantity,
        productId: component.dataset.productId,
        sections: data.sections,
      })
    );

    showSuccess(component);
  } catch (error) {
    console.error('Langley Complete the Look add-to-cart error:', error);
    const message = 'We could not add this item. Please try again.';
    component.dispatchEvent(new CartErrorEvent(form.id || '', message, message, {}));
    setSubmitting(component, false);
  }
}

function attach(root) {
  if (!(root instanceof HTMLElement) || root.dataset.langleyCompleteLookFix === 'true') return;
  root.dataset.langleyCompleteLookFix = 'true';

  root.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const internalControl = target.closest(
      '[data-langley-choose-options], [data-langley-options-close], [data-langley-option-value], [data-langley-qty-minus], [data-langley-qty-plus]'
    );

    if (internalControl && root.contains(internalControl)) {
      event.stopPropagation();
    }
  });

  root.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const component = form.closest(FORM_COMPONENT_SELECTOR);
      if (!(component instanceof HTMLElement) || !root.contains(component)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      void submitCompleteLookForm(root, form, component);
    },
    { capture: true }
  );
}

function attachWithin(node) {
  if (!(node instanceof Element)) return;
  if (node.matches(ROOT_SELECTOR)) attach(node);
  node.querySelectorAll(ROOT_SELECTOR).forEach(attach);
}

document.querySelectorAll(ROOT_SELECTOR).forEach(attach);

const observer = new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      attachWithin(node);
    }
  }
});

observer.observe(document.documentElement, { childList: true, subtree: true });
